import React, {
  Fragment,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';
import FormLabel from 'components/form-label';
import { Control, Controller, Path, useForm } from 'react-hook-form';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import { Switch } from 'components/ui/switch';

interface IChannelConfigProps {
  processorId: string;
  processorName?: string;
  onDirtyChange?: (dirty: boolean) => void;
}

export interface ChannelConfigHandle {
  saveChannelConfig: () => Promise<void>;
}

type ChannelKey = PAYMENT_CONFIG.IChannelKey;
type FlowKey = PAYMENT_CONFIG.IFlowKey;
type ChannelMap = Record<ChannelKey, FlowKey[]>;
type ChannelFlowConfig = PAYMENT_CONFIG.IChannelFlowConfig;
type ChannelFlowConfigNullable = PAYMENT_CONFIG.IChannelFlowConfigNullable;

type FlowState = { flow: Record<FlowKey, boolean> };

type ChannelFormState =
  | false // channel disabled
  | FlowState; // specific flows enabled/disabled
type FormState = Record<ChannelKey, ChannelFormState>;

const FLOW_METADATA: Record<
  ChannelKey | FlowKey,
  { label: string; description: string }
> = {
  // channels
  ffd: {
    label: 'Enable Customer Facing Display',
    description: 'This will enable payments in Customer Facing Display.',
  },
  virtual_terminal: {
    label: 'Enable Virtual Terminal',
    description: 'This will enable payments in virtual terminal.',
  },
  ecommerce: {
    label: 'Enable eCommerce',
    description: 'This will enable payments in eCommerce.',
  },
  hosted_payments: {
    label: 'Enable Hosted Payments',
    description: 'This will enable payments in hosted environment.',
  },
  kiosk: {
    label: 'Enable Kiosk',
    description: 'This will enable payments in Kiosk.',
  },
  pos: {
    label: 'Enable Checkout',
    description: 'This allows this provider to be available at checkout.',
  },
  // flows
  payment_link_redirect: {
    label: 'Enable Payment Link Redirect',
    description: 'This will enable payment link redirect option.',
  },
  payment_link_sms: {
    label: 'Enable Payment Link SMS',
    description: 'This will enable SMS payment link option.',
  },
  payment_link_qr: {
    label: 'Enable Payment Link QR',
    description: 'This will enable QR-code based payment option.',
  },
  terminal_sale: {
    label: 'Enable Payment Terminal',
    description: 'This will enable payments on the physical terminal.',
  },
};

export const applyChannelOverrides = (
  defaultConfigs: ChannelFlowConfig[],
  channelOverrides: ChannelFlowConfigNullable[]
): ChannelMap => {
  // build from defaults
  const map = {} as ChannelMap;
  defaultConfigs?.forEach(({ channel, flows }) => {
    map[channel] = flows;
  });

  channelOverrides?.forEach(({ channel, flows }) => {
    if (Array.isArray(flows)) {
      map[channel] = flows;
    }
  });

  return map;
};

export const mapToFormState = (
  channelMap: Record<ChannelKey, FlowKey[]>,
  defaultChannelFlows: Record<ChannelKey, FlowKey[]>
): FormState => {
  const formState = {} as FormState;

  (Object.keys(defaultChannelFlows) as ChannelKey[])?.forEach((channel) => {
    const allFlows = defaultChannelFlows[channel];
    const enabledFlows = channelMap[channel] ?? [];

    // master OFF: no sub-toggles rendered
    if (enabledFlows.length === 0) {
      formState[channel] = false;
      return;
    }

    // master ON: build full flow object, true for included, false otherwise
    const flowFlags = allFlows.reduce((acc, flow) => {
      acc[flow] = enabledFlows.includes(flow);
      return acc;
    }, {} as Record<FlowKey, boolean>);

    formState[channel] = { flow: flowFlags };
  });

  return formState;
};

export const channelListToMap = (
  list: PAYMENT_CONFIG.IChannelFlowConfig[]
): ChannelMap => {
  return list?.reduce<ChannelMap>((acc, { channel, flows }) => {
    acc[channel] = flows;
    return acc;
  }, {} as ChannelMap);
};

const ChannelToggle = ({
  channel,
  defaultFlows,
  code,
  control,
}: {
  channel: ChannelKey;
  code: ChannelKey | FlowKey;
  control: Control<FormState, any>;
  defaultFlows: FlowKey[];
}) => {
  const metadata = FLOW_METADATA[code];

  if (!metadata?.label) return <Fragment />;

  return (
    <div className="flex justify-between items-start mb-3">
      <div>
        <FormLabel
          element={channel}
          label={metadata.label}
          className="text-base font-semibold text-gray-blue-80"
        />
        {metadata.description && (
          <p className="text-sm font-normal leading-5 text-gray-blue-70">{metadata.description}</p>
        )}
      </div>
      <Controller
        name={channel}
        control={control}
        render={({ field }) => {
          const isChecked = field.value !== false;
          return (
            <Switch
              id={channel}
              checked={isChecked}
              onCheckedChange={(checked) => {
                if (checked) {
                  const allTrue = defaultFlows.reduce<
                    Record<FlowKey, boolean>
                  >((acc, flowKey) => {
                    acc[flowKey] = true;
                    return acc;
                  }, {} as Record<FlowKey, boolean>);
                  field.onChange({ flow: allTrue });
                } else {
                  field.onChange(false);
                }
              }}
              className="mt-1"
            />
          );
        }}
      />
    </div>
  );
};

const FlowToggle = ({
  channel,
  flow,
  code,
  control,
}: {
  channel: ChannelKey;
  flow: FlowKey;
  code: ChannelKey | FlowKey;
  control: Control<FormState, any>;
}) => {
  const metadata = FLOW_METADATA[code];
  const elemName = `${channel}.flow.${flow}` as Path<FormState>;

  if (!metadata?.label) return <Fragment />;

  return (
    <div className="flex justify-between items-start mb-3">
      <div>
        <FormLabel
          element={elemName}
          label={metadata.label}
          className="text-base font-semibold text-gray-blue-80"
        />
        {metadata.description && (
          <p className="text-sm font-normal leading-5 text-gray-blue-70">{metadata.description}</p>
        )}
      </div>
      <Controller
        name={elemName}
        control={control}
        render={({ field }) => {
          const isChecked = field.value as boolean;
          return (
            <Switch
              id={elemName}
              checked={isChecked}
              onCheckedChange={field.onChange}
              className="mt-1"
            />
          );
        }}
      />
    </div>
  );
};

const ChannelConfigurations = forwardRef<
  ChannelConfigHandle,
  IChannelConfigProps
>(({ processorId, processorName, onDirtyChange }, ref) => {
  const {
    paymentConfig,
    toUpdateChannelConfiguration,
    saveGlobalCardReaderValues,
    updateGlobalCardReaderValues,
  } = usePaymentConfig();
  const {
    paymentConfiguration,
    channelConfiguration,
    isLoading,
    getCardReaderValues,
  } = paymentConfig;

  const form = useForm<FormState>({
    defaultValues: {} as FormState,
  });
  const {
    control,
    watch,
    getValues,
    formState: { isDirty, dirtyFields },
    reset,
  } = form;
  const watchAll = watch();

  const defaultList = paymentConfiguration?.defaultChannelConfig || [];
  const overrideList = channelConfiguration || [];
  const defaultChannelMap = useMemo(
    () => channelListToMap(defaultList),
    [defaultList]
  );
  const mergedMap = useMemo(
    () => applyChannelOverrides(defaultList, overrideList),
    [defaultList, channelConfiguration]
  );

  useEffect(() => {
    if (
      !isLoading &&
      Array.isArray(paymentConfiguration.defaultChannelConfig) &&
      Array.isArray(channelConfiguration)
    ) {
      const initialValues = mapToFormState(mergedMap, defaultChannelMap);
      reset(initialValues);
    }
  }, [
    isLoading,
    paymentConfiguration.defaultChannelConfig,
    channelConfiguration,
    mergedMap,
    defaultChannelMap,
    reset,
  ]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]);

  useImperativeHandle(ref, () => ({
    // action on save
    async saveChannelConfig() {
      const vals = getValues();
      const payload = (Object.keys(vals) as ChannelKey[])
        .filter((ch) => dirtyFields[ch])
        .map((channel) => {
          const state = vals[channel];
          if (state === false) return { channel, flows: [] };
          const flows = Object.entries(state.flow)
            .filter(([, on]) => on)
            .map(([f]) => f as FlowKey);
          return { channel, flows };
        });

      await toUpdateChannelConfiguration({
        processorId,
        channelFlows: payload,
      });

      // TEMPORARY MIGRATION CODE: Dual-write to entity config for backward compatibility
      // TODO: Remove this once all UIs are migrated to use POS channel config
      const posChannel = payload.find((ch) => ch.channel === 'pos');
      if (posChannel && processorName && getCardReaderValues?.items) {
        try {
          const isEnabled = posChannel.flows.length > 0;
          const existingConfig = getCardReaderValues.items.find(
            (item) => item.code === 'isCheckoutEnabled'
          );

          const entityConfigPayload: PAYMENT_CONFIG.IGlobalCardReader = {
            id: existingConfig?.id,
            name: `${processorName}_isCheckoutEnabled`,
            category: null,
            code: 'isCheckoutEnabled',
            value: isEnabled.toString(),
            isSecret: false,
            isRequired: false,
            processorId,
          };

          if (existingConfig?.id) {
            await updateGlobalCardReaderValues(entityConfigPayload);
          } else {
            await saveGlobalCardReaderValues(entityConfigPayload);
          }
        } catch (err) {
          console.warn(
            '[migration] Failed to dual-write isCheckoutEnabled:',
            err
          );
        }
      }
    },
  }));

  if (
    isLoading ||
    !paymentConfiguration.defaultChannelConfig?.length ||
    !Array.isArray(channelConfiguration)
  ) {
    return <Fragment />;
  }

  return (
    <div className="max-w-3xl">
      {defaultChannelMap &&
        (Object.keys(defaultChannelMap) as ChannelKey[])?.map((channel) => {
          const flows = defaultChannelMap[channel];
          const isMulti = flows.length > 1;
          const channelState = watchAll?.[channel];
          const masterOn =
            typeof channelState === 'object' && channelState !== null;

          return (
            <div key={channel} className="mb-3">
              <ChannelToggle
                code={channel}
                control={control}
                channel={channel}
                defaultFlows={defaultChannelMap[channel]}
              />
              {isMulti && masterOn && (
                <div className="bg-gray-50 px-6 py-3">
                  {flows.map((flow) => (
                    <FlowToggle
                      key={flow}
                      code={flow}
                      control={control}
                      channel={channel}
                      flow={flow}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
});

export default ChannelConfigurations;
